// api/databaseClient.js
import axios from 'axios';
import TokenService from '../services/token.service';

/**
 * Cliente HTTP configurado para el Database Service
 * (tablas SQL/NoSQL, registros, queries).
 *
 * Base URL: `REACT_APP_DATABASE_API_URL` — en dev apunta al invoke URL
 * crudo del API Gateway (`https://<id>.execute-api.us-west-2.amazonaws.com/v1`)
 * porque el dominio custom (`database-dev.zenitailab.com`) está reservado
 * en Terraform pero aún no aplicado. Cuando se aplique, basta cambiar el
 * env var; los paths de las rutas no se tocan.
 *
 * Auth: Bearer JWT (mismo Cognito user pool que los otros services).
 *
 * Header `x-account-id`: lo enviamos siempre que esté disponible en
 * `localStorage.currentAccountId`. El backend lee el account_id del path
 * (`/accounts/{id}/...`) pero el header se manda para alinear con el
 * patrón del resto de services account-scoped (mismo que `connectionsClient`
 * y `billingClient`).
 */
const databaseClient = axios.create({
  baseURL: process.env.REACT_APP_DATABASE_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

databaseClient.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const accountId = localStorage.getItem('currentAccountId');
    if (accountId) {
      config.headers['x-account-id'] = accountId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Extrae `{code, message}` de un cuerpo de error del database_service,
 * que ha llegado en al menos tres shapes distintos según el endpoint:
 *   - nuevo (validación uniforme, ej. sort_by/sort_direction inválidos) →
 *     `{ detail: { code, message, request_id } }`
 *   - legacy plano → `{ error, message }`
 *   - legacy plano alterno → `{ code, message }`
 *
 * Centralizar esto evita que `data.detail` (un objeto en el shape nuevo)
 * se use directo como `message` — error real visto en code review de la
 * MR de sort/filtro (deuda técnica #007): sin este helper, un 400
 * VALIDATION_FAILED nuevo renderiza "[object Object]" en vez del mensaje.
 */
const extractErrorInfo = (data, fallbackMessage) => {
  const detail = data?.detail;
  if (detail && typeof detail === 'object') {
    return {
      code: detail.code ?? null,
      message: detail.message ?? fallbackMessage,
    };
  }
  return {
    code: data?.error ?? data?.code ?? null,
    message: data?.message ?? (typeof detail === 'string' ? detail : fallbackMessage),
  };
};

databaseClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('Database API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
      // Push monthly-quota (402) and burst-limit (429) errors into the
      // global banner store so the user sees a persistent reminder
      // instead of just a transient toast. The store is consumed by
      // `QuotaBanner` mounted above `DatabaseView`.
      const status = error.response.status;
      if (status === 402 || status === 429) {
        const data = error.response.data ?? {};
        const { code, message } = extractErrorInfo(data, 'Plan limit reached');
        // Lazy import — `useQuotaState` is in the FE bundle but
        // pulling it at module-eval time would form a cycle with the
        // services layer that imports `databaseClient`.
        import('../hooks/useQuotaState').then(({ setQuotaError }) => {
          setQuotaError({ status, code, message, raw: data });
        });
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Normaliza errores del database_service a `{status, message, code, data}`.
 *
 * El backend devuelve cuerpos heterogéneos según el caso — ver
 * `extractErrorInfo` para los shapes soportados.
 *
 * Esta función deja un shape uniforme para que los callers no tengan que
 * inspeccionar `error.response` cada vez.
 */
export const handleDatabaseError = (error) => {
  if (error.response) {
    const data = error.response.data ?? {};
    const { code, message } = extractErrorInfo(data, 'Database service error');
    return {
      status: error.response.status,
      code,
      message,
      data,
    };
  }
  if (error.request) {
    return {
      status: 0,
      code: 'NETWORK',
      message: 'Could not reach the database service',
      data: null,
    };
  }
  return {
    status: 0,
    code: 'UNKNOWN',
    message: error.message ?? 'Unknown error',
    data: null,
  };
};

export default databaseClient;
